import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  ApiClubCourt,
  ApiCourtBlock,
  ApiMatchPlanner,
  ApiSchedulePublication,
  AssignCourtSlotPayload,
  CourtBlockPayload,
  CreateCourtPayload,
  SchedulePublicationPayload,
  UpdateCourtPayload,
} from '../model/courtTypes';
import type { ApiMatch } from '../model/types';
import {
  addCourtBlock,
  assignCourtSlot,
  clearCourtSlot,
  createCourt,
  deleteCourt,
  getClubCourts,
  getMatchPlanner,
  getSchedulePublication,
  removeCourtBlock,
  setSchedulePublication,
  updateCourt,
} from './courtsApi';

/** Klucze pamieci podrecznej kortow klubu i plannera. */
export const courtKeys = {
  all: ['courts'] as const,
  list: (clubId: string) => [...courtKeys.all, 'list', clubId] as const,
  planner: (clubId: string, date: string) => [...courtKeys.all, 'planner', clubId, date] as const,
  publication: (clubId: string) => [...courtKeys.all, 'publication', clubId] as const,
};

/** Pobiera korty klubu wraz z przerwami technicznymi. */
export function useClubCourtsQuery(clubId: string | undefined): UseQueryResult<ApiClubCourt[]> {
  return useQuery({
    queryKey: courtKeys.list(clubId ?? ''),
    queryFn: () => getClubCourts(clubId ?? ''),
    enabled: Boolean(clubId),
  });
}

/** Pobiera kalendarz planowania meczow klubu na wskazany dzien. */
export function useMatchPlannerQuery(
  clubId: string | undefined,
  date: string,
): UseQueryResult<ApiMatchPlanner> {
  return useQuery({
    queryKey: courtKeys.planner(clubId ?? '', date),
    queryFn: () => getMatchPlanner(clubId ?? '', date),
    enabled: Boolean(clubId),
  });
}

/**
 * Uniewaznia wszystko, co zalezy od kortow i slotow meczow. Celowo szeroko:
 * przypisanie slotu zmienia planner, liste kortow, terminarz i mecze rozgrywki.
 */
function invalidatePlanning(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: courtKeys.all });
  void queryClient.invalidateQueries({ queryKey: ['competitions'] });
}

/** Zaklada kort klubu. */
export function useCreateCourtMutation(
  clubId: string,
): UseMutationResult<ApiClubCourt, Error, CreateCourtPayload> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateCourtPayload) => createCourt(clubId, payload),
    onSuccess: () => invalidatePlanning(queryClient),
  });
}

/** Zmienne mutacji edycji kortu. */
export interface UpdateCourtVariables {
  courtId: string;
  payload: UpdateCourtPayload;
}

/** Edytuje kort klubu (numer, nazwa, nawierzchnia). */
export function useUpdateCourtMutation(
  clubId: string,
): UseMutationResult<void, Error, UpdateCourtVariables> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ courtId, payload }: UpdateCourtVariables) =>
      updateCourt(clubId, courtId, payload),
    onSuccess: () => invalidatePlanning(queryClient),
  });
}

/** Usuwa kort klubu. Kort z zaplanowanymi meczami odrzuca 409. */
export function useDeleteCourtMutation(clubId: string): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (courtId: string) => deleteCourt(clubId, courtId),
    onSuccess: () => invalidatePlanning(queryClient),
  });
}

/** Zmienne mutacji dodania przerwy technicznej. */
export interface AddBlockVariables {
  courtId: string;
  payload: CourtBlockPayload;
}

/** Dodaje przerwe techniczna na korcie. */
export function useAddCourtBlockMutation(
  clubId: string,
): UseMutationResult<ApiCourtBlock, Error, AddBlockVariables> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ courtId, payload }: AddBlockVariables) =>
      addCourtBlock(clubId, courtId, payload),
    onSuccess: () => invalidatePlanning(queryClient),
  });
}

/** Zmienne mutacji usuniecia przerwy technicznej. */
export interface RemoveBlockVariables {
  courtId: string;
  blockId: string;
}

/** Usuwa przerwe techniczna z kortu. */
export function useRemoveCourtBlockMutation(
  clubId: string,
): UseMutationResult<void, Error, RemoveBlockVariables> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ courtId, blockId }: RemoveBlockVariables) =>
      removeCourtBlock(clubId, courtId, blockId),
    onSuccess: () => invalidatePlanning(queryClient),
  });
}

/** Zmienne mutacji kladacej mecz na slocie kalendarza. */
export interface AssignSlotVariables {
  matchId: string;
  payload: AssignCourtSlotPayload;
}

/**
 * Kladzie mecz na korcie (drop na kalendarzu). Kolizja z innym meczem albo
 * przerwa techniczna konczy sie bledem 409 z komunikatem z backendu.
 */
export function useAssignCourtSlotMutation(): UseMutationResult<
  ApiMatch,
  Error,
  AssignSlotVariables
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ matchId, payload }: AssignSlotVariables) => assignCourtSlot(matchId, payload),
    onSuccess: () => invalidatePlanning(queryClient),
  });
}

/** Zdejmuje mecz z kalendarza — wraca do puli do rozplanowania. */
export function useClearCourtSlotMutation(): UseMutationResult<ApiMatch, Error, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (matchId: string) => clearCourtSlot(matchId),
    onSuccess: () => invalidatePlanning(queryClient),
  });
}

/** Pobiera widocznosc planu meczow klubu na ekranie prezentacji. */
export function useSchedulePublicationQuery(
  clubId: string | undefined,
): UseQueryResult<ApiSchedulePublication> {
  return useQuery({
    queryKey: courtKeys.publication(clubId ?? ''),
    queryFn: () => getSchedulePublication(clubId ?? ''),
    enabled: Boolean(clubId),
  });
}

/**
 * Upublicznia albo utajnia plan meczow klubu.
 *
 * Odpowiedz wstawiamy prosto do pamieci podrecznej, zeby przycisk przelaczyl
 * sie od razu — bez tego etykieta „Upublicznij”/„Utajnij” mrugalaby do czasu
 * powrotu zapytania.
 */
export function useSetSchedulePublicationMutation(
  clubId: string,
): UseMutationResult<ApiSchedulePublication, Error, SchedulePublicationPayload> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: SchedulePublicationPayload) => setSchedulePublication(clubId, payload),
    onSuccess: (publication) => {
      queryClient.setQueryData(courtKeys.publication(clubId), publication);
    },
  });
}
